import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Snackbar,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useCerberus } from "../../hooks/useCerberus";
import { getUserTimezone, setUserTimezone } from "../../utils/dateUtils";
import useApi from "../../hooks/useApi";
import { audienceForRoles } from "../Access/audience";

interface TimezoneOption {
  value: string;
  label: string;
}

const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
];

export default function TimezoneDetection() {
  const { cerberusUser, roles } = useCerberus();
  // GET/POST /profile carry no [AllowExternalUser] (ProfileController): external users would be
  // offered an update the server refuses. Caregivers and patients are allowed.
  const canUpdateProfile = audienceForRoles(roles) !== "external";
  const api = useApi();
  const [showNotification, setShowNotification] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Only check for timezone mismatch if user is authenticated and has a timezone set
    if (cerberusUser?.timeZone && canUpdateProfile) {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userTimezone = getUserTimezone();
      
      // Check if browser timezone differs from user's saved timezone
      if (browserTimezone !== userTimezone) {
        // Check if user has already dismissed this notification for this specific mismatch
        const dismissedKey = `timezone_dismissed_${browserTimezone}_${userTimezone}`;
        const hasDismissed = localStorage.getItem(dismissedKey);
        
        if (!hasDismissed) {
          setShowNotification(true);
        }
      }
    }
  }, [cerberusUser?.timeZone, canUpdateProfile]);

  const handleUpdateTimezone = async () => {
    if (!selectedTimezone) return;

    setIsUpdating(true);
    try {
      // First, get the current user profile data
      const currentProfile = await api.get("/profile");
      
      if (currentProfile.status !== 200) {
        throw new Error("Failed to get current profile data");
      }

      // Update the profile data with the new timezone
      const profileData = {
        ...currentProfile.data,
        timeZone: selectedTimezone,
      };

      // Send the complete profile data with updated timezone
      await api.post("/profile", profileData);
      
      // Update localStorage
      setUserTimezone(selectedTimezone);
      
      // Clear any dismissed timezone notifications since user has updated their preference
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('timezone_dismissed_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Close dialogs and hide notification
      setShowDialog(false);
      setShowNotification(false);
      
      // Refresh user data to get updated timezone
      window.location.reload();
    } catch (error) {
      console.error("Failed to update timezone:", error);
      // Show a more user-friendly error message
      alert("Failed to update timezone. Please try updating your timezone in your profile settings instead.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenDialog = () => {
    setSelectedTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    setShowDialog(true);
    setShowNotification(false);
  };

  const handleCloseNotification = () => {
    // Mark this specific timezone mismatch as dismissed
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userTimezone = getUserTimezone();
    const dismissedKey = `timezone_dismissed_${browserTimezone}_${userTimezone}`;
    localStorage.setItem(dismissedKey, "true");
    
    setShowNotification(false);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  const getCurrentBrowserTimezoneLabel = () => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const option = TIMEZONE_OPTIONS.find(opt => opt.value === browserTimezone);
    return option?.label || browserTimezone;
  };

  const getUserTimezoneLabel = () => {
    const userTimezone = getUserTimezone();
    const option = TIMEZONE_OPTIONS.find(opt => opt.value === userTimezone);
    return option?.label || userTimezone;
  };

  return (
    <>
      {/* Snackbar notification */}
      <Snackbar
        open={showNotification}
        autoHideDuration={10000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity="info"
          variant="filled"
          sx={{ width: "100%", maxWidth: 600 }}
          action={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleOpenDialog}
                sx={{ fontWeight: 600 }}
              >
                Update
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={handleCloseNotification}
                sx={{ fontWeight: 600 }}
              >
                Dismiss
              </Button>
            </Box>
          }
        >
          <Typography variant="body2">
            Your browser timezone ({getCurrentBrowserTimezoneLabel()}) differs from your saved preference ({getUserTimezoneLabel()}). 
            Would you like to update your timezone to match your current location?
          </Typography>
        </Alert>
      </Snackbar>

      {/* Dialog for timezone selection */}
      <Dialog open={showDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Timezone</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            We detected that your browser timezone ({getCurrentBrowserTimezoneLabel()}) differs from your saved preference ({getUserTimezoneLabel()}). 
            Please select your preferred timezone:
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={selectedTimezone}
              label="Timezone"
              onChange={(e) => setSelectedTimezone(e.target.value)}
            >
              {TIMEZONE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isUpdating}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateTimezone} 
            variant="contained" 
            disabled={isUpdating || !selectedTimezone}
          >
            {isUpdating ? "Updating..." : "Update Timezone"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
} 